import { Component, Input, OnInit } from '@angular/core';
import { IntegrationStatus } from '../../../models/integration.model';

/**
 * Integration Status Indicator Component
 * Displays the integration status of a project
 * Following existing component patterns in the codebase
 */
@Component({
  selector: 'app-integration-status-indicator',
  templateUrl: './integration-status-indicator.component.html',
  styleUrls: ['./integration-status-indicator.component.scss'],
})
export class IntegrationStatusIndicatorComponent implements OnInit {
  // Input properties
  @Input() status: IntegrationStatus | null = null;
  @Input() showDetails: boolean = false;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';

  constructor() {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    // Initialize component - validate input status
    if (this.status && !this.status.sync_status) {
      console.warn('Integration status provided without sync_status field');
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get status CSS class
   */
  getStatusClass(): string {
    if (!this.status) return 'status-unknown';

    switch (this.status.sync_status) {
      case 'synced':
        return 'status-synced';
      case 'pending':
        return 'status-pending';
      case 'failed':
        return 'status-failed';
      default:
        return 'status-never-synced';
    }
  }

  /**
   * Get status icon
   */
  getStatusIcon(): string {
    if (!this.status) return 'help_outline';

    switch (this.status.sync_status) {
      case 'synced':
        return 'check_circle';
      case 'pending':
        return 'schedule';
      case 'failed':
        return 'error';
      default:
        return 'remove_circle';
    }
  }

  /**
   * Get status text
   */
  getStatusText(): string {
    if (!this.status) return 'Unknown';

    switch (this.status.sync_status) {
      case 'synced':
        return 'Synced';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      default:
        return 'Not Synced';
    }
  }

  /**
   * Get status color
   */
  getStatusColor(): string {
    if (!this.status) return '#9e9e9e';

    switch (this.status.sync_status) {
      case 'synced':
        return '#4caf50';
      case 'pending':
        return '#ff9800';
      case 'failed':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  }

  /**
   * Check if project is synced from Unifier
   */
  isSyncedFromUnifier(): boolean {
    return this.status?.is_synced_from_unifier || false;
  }

  /**
   * Get formatted last sync time
   */
  getLastSyncTime(): string {
    if (!this.status?.unifier_updated_at) return 'Never';

    const date = new Date(this.status.unifier_updated_at);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  /**
   * Get size class
   */
  getSizeClass(): string {
    return `size-${this.size}`;
  }
}
